/**
 * SEO URL Classification Registry — Phase 1
 *
 * Central registry for every URL's indexing state, sitemap group,
 * and active submission status.
 *
 * IndexStatus: 'index' | 'noindex' | 'redirect'
 *   - index: page is indexable (may be core or secondary based on sitemapGroup)
 *   - noindex: page is live but has <meta name="robots" content="noindex, follow">
 *   - redirect: page redirects to another via client-side Navigate
 *
 * SitemapGroup: architectural grouping for sitemap files
 * SubmissionStatus: whether the sitemap group is actively submitted in sitemap-index.xml
 */

export type IndexStatus = 'index' | 'noindex' | 'redirect';
export type SitemapGroup = 'core' | 'tools-core' | 'tools-secondary' | 'blog' | 'legal' | 'landing' | 'none';
export type SubmissionStatus = 'submit-now' | 'hold' | 'exclude';

export interface URLClassification {
  path: string;
  cluster: string;
  primaryIntent: string;
  indexStatus: IndexStatus;
  sitemapGroup: SitemapGroup;
  submissionStatus: SubmissionStatus;
  canonicalTarget?: string;   // Only for noindex pages
  redirectTarget?: string;    // Only for redirect pages
  priorityTier: number;       // 0.0–1.0 for sitemap priority
  lastmod: string;
}

const TODAY = '2026-03-19';

// ─── 23 Core Tools (submit-now) ─────────────────────────────────────────────
const coreToolSlugs = new Set([
  'comprimir-jpg',
  'comprimir-png',
  'comprimir-webp',
  'comprimir-imagen-sin-perder-calidad',
  'comprimir-imagen-para-web',
  'convertir-webp-jpg',
  'convertir-jpg-webp',
  'convertir-png-jpg',
  'convertir-jpg-png',
  'convertir-heic-jpg',
  'convertir-imagen-pdf',
  'reducir-imagen-200kb',
  'reducir-imagen-100kb',
  'reducir-imagen-50kb',
  'reducir-imagen-500kb',
  'redimensionar-imagen',
  'redimensionar-imagen-instagram',
  'recortar-imagen',
  'rotar-imagen',
  'optimizar-imagen-online',
  'optimizar-imagen-para-web',
  'mejorar-calidad-imagen',
]);

// ─── Noindex pages (live but noindex, follow) ───────────────────────────────
// Only pages with unique seoContent or core designation are indexed.
// All other tool pages are noindex to eliminate thin-content signal.
const noindexPages: Record<string, string> = {
  // Previously noindexed (retain)
  'optimizar-imagen-seo':            '/herramientas/optimizar-imagen-para-web',
  'optimizar-imagen-para-blog':      '/herramientas/optimizar-imagen-para-web',
  'comprimir-imagen-para-web-gratis':'/herramientas/comprimir-imagen-para-web',
  'comprimir-imagen-gratis':         '/comprimir-imagen',
  // Note: redimensionar-imagen-dni, reducir-imagen-25kb, redimensionar-imagen-800x600
  // removed from noindex — they have unique seoContent and are now indexed.

  // Compression — platform/use-case variants (no unique content)
  'comprimir-imagen-para-email':      '/herramientas/comprimir-jpg',
  'comprimir-imagen-para-formulario': '/herramientas/comprimir-jpg',
  'comprimir-imagen-para-pdf':        '/herramientas/convertir-imagen-pdf',
  'comprimir-imagen-para-instagram':  '/herramientas/redimensionar-imagen-instagram',
  'comprimir-imagen-para-linkedin':   '/herramientas/comprimir-jpg',
  'comprimir-imagen-para-twitter':    '/herramientas/redimensionar-imagen-twitter',
  'comprimir-foto-whatsapp':          '/herramientas/comprimir-jpg',
  'comprimir-imagen-para-whatsapp':   '/herramientas/comprimir-jpg',
  'comprimir-varias-imagenes':        '/herramientas/comprimir-jpg',
  'comprimir-imagen-para-cv':         '/herramientas/comprimir-jpg',

  // Conversion — format variants without unique content
  'convertir-heic-png':  '/herramientas/convertir-heic-jpg',
  'convertir-bmp-jpg':   '/herramientas/comprimir-jpg',
  'convertir-bmp-png':   '/herramientas/comprimir-png',
  'convertir-gif-jpg':   '/herramientas/comprimir-gif',
  'convertir-gif-png':   '/herramientas/comprimir-gif',
  'convertir-svg-png':   '/herramientas/comprimir-png',
  'convertir-tiff-jpg':  '/herramientas/comprimir-jpg',
  'convertir-avif-jpg':  '/herramientas/convertir-webp-jpg',
  'convertir-avif-png':  '/herramientas/convertir-png-jpg',
  'imagenes-a-pdf':      '/herramientas/convertir-imagen-pdf',
  'jpg-a-pdf':           '/herramientas/convertir-imagen-pdf',
  'png-a-pdf':           '/herramientas/convertir-imagen-pdf',

  // Resize — social/platform variants without unique content
  'redimensionar-imagen-linkedin':      '/herramientas/redimensionar-imagen-instagram',
  'redimensionar-imagen-youtube':       '/herramientas/redimensionar-imagen-instagram',
  'redimensionar-imagen-pinterest':     '/herramientas/redimensionar-imagen-instagram',
  'redimensionar-imagen-tiktok':        '/herramientas/redimensionar-imagen-instagram',
  'redimensionar-imagen-pasaporte':     '/herramientas/redimensionar-imagen-dni',
  'redimensionar-imagen-carnet':        '/herramientas/redimensionar-imagen-dni',
  'redimensionar-imagen-para-web':      '/herramientas/optimizar-imagen-para-web',
  'redimensionar-imagen-para-whatsapp': '/herramientas/redimensionar-imagen',
  'redimensionar-imagen-para-cv':       '/herramientas/redimensionar-imagen',
  'redimensionar-imagen-25-porciento':  '/herramientas/redimensionar-imagen',
  'redimensionar-imagen-50-porciento':  '/herramientas/redimensionar-imagen',
  'aumentar-tamano-imagen':             '/herramientas/redimensionar-imagen',

  // Rotate/flip variants
  'rotar-imagen-90-grados': '/herramientas/rotar-imagen',
  'voltear-imagen':          '/herramientas/rotar-imagen',

  // Crop variants
  'recortar-imagen-instagram': '/herramientas/recortar-imagen',
  'recortar-imagen-cuadrada':  '/herramientas/recortar-imagen',
  'recortar-imagen-16-9':      '/herramientas/recortar-imagen',

  // Target-size reducir-imagen variants without unique seoContent
  'reducir-imagen-10kb':           '/herramientas/reducir-imagen-25kb',
  'reducir-imagen-20kb':           '/herramientas/reducir-imagen-25kb',
  'reducir-imagen-30kb':           '/herramientas/reducir-imagen-25kb',
  'reducir-imagen-150kb':          '/herramientas/reducir-imagen-200kb',
  'reducir-imagen-250kb':          '/herramientas/reducir-imagen-200kb',
  'reducir-imagen-300kb':          '/herramientas/reducir-imagen-500kb',
  'reducir-imagen-3mb':            '/herramientas/reducir-imagen-2mb',
  'reducir-imagen-5mb':            '/herramientas/reducir-imagen-2mb',
  'reducir-foto-100kb':            '/herramientas/reducir-imagen-100kb',
  'reducir-imagen-para-formulario':'/herramientas/reducir-imagen-200kb',
  'reducir-imagen-para-cv':        '/herramientas/comprimir-jpg',

  // Adjust/enhance variants without unique seoContent
  'ajustar-brillo-imagen':     '/herramientas/mejorar-calidad-imagen',
  'ajustar-contraste-imagen':  '/herramientas/mejorar-calidad-imagen',
  'enfocar-imagen':            '/herramientas/mejorar-foto-borrosa',
  'suavizar-imagen':           '/herramientas/mejorar-calidad-imagen',
  'quitar-ruido-imagen':       '/herramientas/mejorar-foto-borrosa',
  'eliminar-grano-imagen':     '/herramientas/mejorar-foto-borrosa',
  'aclarar-imagen-oscura':     '/herramientas/mejorar-calidad-imagen',
  'mejorar-calidad-foto-online':'/herramientas/mejorar-calidad-imagen',

  // CMS optimization platform variants
  'optimizar-imagen-wordpress':   '/herramientas/optimizar-imagen-para-web',
  'optimizar-imagen-shopify':     '/herramientas/optimizar-imagen-para-web',
  'optimizar-imagen-woocommerce': '/herramientas/optimizar-imagen-para-web',
};

// ─── Redirect pages (from redirectMap.ts) ───────────────────────────────────
import { redirectMap } from './redirectMap';

// ─── All tool slugs (manual + generated) ────────────────────────────────────
// This list is maintained to classify every known tool slug.
const allToolSlugs: string[] = [
  // Manual tools (66)
  'comprimir-jpg', 'comprimir-png', 'comprimir-webp',
  'comprimir-imagen', 'comprimir-foto', 'comprimir-imagen-online',
  'comprimir-imagen-sin-perder-calidad',
  'comprimir-imagen-para-email', 'comprimir-imagen-para-web',
  'comprimir-imagen-para-formulario', 'comprimir-imagen-para-pdf',
  'comprimir-imagen-para-instagram', 'comprimir-imagen-para-linkedin',
  'comprimir-imagen-para-twitter', 'comprimir-foto-whatsapp',
  'convertir-webp-jpg', 'convertir-jpg-webp', 'convertir-png-jpg',
  'convertir-jpg-png', 'convertir-webp-png', 'convertir-png-webp',
  'convertir-heic-jpg', 'convertir-heic-png', 'convertir-bmp-jpg',
  'convertir-bmp-png', 'convertir-gif-jpg', 'convertir-gif-png',
  'convertir-svg-png', 'convertir-tiff-jpg',
  'redimensionar-imagen', 'redimensionar-imagen-1080x1080',
  'redimensionar-imagen-1920x1080', 'redimensionar-imagen-800x600',
  'redimensionar-imagen-instagram', 'redimensionar-imagen-facebook',
  'redimensionar-imagen-twitter', 'redimensionar-imagen-linkedin',
  'redimensionar-imagen-youtube', 'redimensionar-imagen-pinterest',
  'redimensionar-imagen-tiktok', 'redimensionar-imagen-pasaporte',
  'redimensionar-imagen-dni', 'redimensionar-imagen-carnet',
  'redimensionar-foto-pasaporte', 'redimensionar-foto-carnet',
  'redimensionar-imagen-para-web',
  'optimizar-imagen-online', 'optimizar-imagen-para-web',
  'optimizar-imagen-wordpress', 'optimizar-imagen-shopify',
  'optimizar-imagen-woocommerce',
  'reducir-imagen-200kb', 'reducir-imagen-100kb', 'reducir-imagen-50kb',
  'reducir-imagen-20kb', 'reducir-imagen-10kb', 'reducir-imagen-500kb',
  'reducir-imagen-300kb', 'reducir-imagen-150kb', 'reducir-imagen-250kb',
  'reducir-imagen-25kb', 'reducir-imagen-2mb', 'reducir-imagen-5mb',
  'reducir-foto-200kb', 'reducir-foto-100kb',
  'reducir-imagen-para-formulario',
  'comprimir-imagen-para-cv',
  // Generated tools (Phase 1–4)
  'reducir-imagen-30kb', 'reducir-imagen-75kb', 'reducir-imagen-1mb', 'reducir-imagen-3mb',
  'reducir-foto-50kb', 'reducir-foto-300kb', 'reducir-foto-500kb',
  'comprimir-fotos-online', 'comprimir-imagen-gratis',
  'reducir-peso-imagen', 'comprimir-foto-online', 'comprimir-imagen-jpg',
  'reducir-tamano-imagen', 'reducir-peso-foto',
  'comprimir-imagen-para-whatsapp', 'comprimir-imagen-para-web-gratis',
  'optimizar-imagen-seo', 'optimizar-imagen-para-blog',
  'recortar-imagen', 'recortar-imagen-online', 'recortar-imagen-instagram',
  'recortar-imagen-cuadrada', 'recortar-imagen-16-9',
  'rotar-imagen', 'girar-foto', 'voltear-imagen', 'rotar-imagen-90-grados',
  'redimensionar-imagen-50-porciento', 'redimensionar-imagen-25-porciento',
  'aumentar-tamano-imagen',
  'comprimir-varias-imagenes',
  'ajustar-brillo-imagen', 'ajustar-contraste-imagen',
  'mejorar-calidad-imagen', 'enfocar-imagen', 'suavizar-imagen',
  'convertir-imagen-pdf', 'imagenes-a-pdf', 'jpg-a-pdf', 'png-a-pdf',
  'convertir-avif-jpg', 'convertir-avif-png',
  'reducir-imagen-para-cv',
  'quitar-ruido-imagen', 'eliminar-grano-imagen',
  'mejorar-foto-borrosa', 'aclarar-imagen-oscura', 'mejorar-calidad-foto-online',
  'redimensionar-imagen-para-whatsapp', 'redimensionar-imagen-para-cv',
];

// ─── Build classification entries for tools ─────────────────────────────────
function classifyTool(slug: string): URLClassification {
  const path = `/herramientas/${slug}`;

  // Check redirect first
  if (redirectMap[slug]) {
    return {
      path,
      cluster: getCluster(slug),
      primaryIntent: slug,
      indexStatus: 'redirect',
      sitemapGroup: 'none',
      submissionStatus: 'exclude',
      redirectTarget: redirectMap[slug].startsWith('/') ? redirectMap[slug] : `/herramientas/${redirectMap[slug]}`,
      priorityTier: 0,
      lastmod: TODAY,
    };
  }

  // Check noindex
  if (noindexPages[slug]) {
    return {
      path,
      cluster: getCluster(slug),
      primaryIntent: slug,
      indexStatus: 'noindex',
      sitemapGroup: 'tools-secondary',
      submissionStatus: 'exclude',
      canonicalTarget: noindexPages[slug],
      priorityTier: 0.3,
      lastmod: TODAY,
    };
  }

  // Core or secondary
  const isCore = coreToolSlugs.has(slug);
  return {
    path,
    cluster: getCluster(slug),
    primaryIntent: slug,
    indexStatus: 'index',
    sitemapGroup: isCore ? 'tools-core' : 'tools-secondary',
    submissionStatus: isCore ? 'submit-now' : 'hold',
    priorityTier: isCore ? 0.8 : 0.5,
    lastmod: TODAY,
  };
}

function getCluster(slug: string): string {
  if (slug.startsWith('comprimir-') || slug.startsWith('reducir-peso') || slug.startsWith('reducir-tamano')) return 'compression';
  if (slug.startsWith('convertir-') || slug.startsWith('imagenes-a-') || slug.endsWith('-a-pdf')) return 'conversion';
  if (slug.startsWith('redimensionar-')) return 'resize';
  if (slug.startsWith('reducir-imagen-para-') || slug.startsWith('reducir-foto-para-')) return 'target-use-case';
  if (slug.startsWith('reducir-imagen-') || slug.startsWith('reducir-foto-')) return 'target-size';
  if (slug.startsWith('recortar-')) return 'crop';
  if (slug.startsWith('rotar-') || slug.startsWith('girar-') || slug.startsWith('voltear-')) return 'rotate';
  if (slug.startsWith('optimizar-')) return 'optimization';
  if (slug.startsWith('ajustar-') || slug.startsWith('mejorar-') || slug.startsWith('enfocar-') || slug.startsWith('suavizar-') || slug.startsWith('quitar-') || slug.startsWith('eliminar-') || slug.startsWith('aclarar-')) return 'adjust';
  if (slug.startsWith('aumentar-')) return 'resize-percent';
  return 'other';
}

// ─── Static pages ───────────────────────────────────────────────────────────
const staticPages: URLClassification[] = [
  { path: '/', cluster: 'home', primaryIntent: 'home', indexStatus: 'index', sitemapGroup: 'core', submissionStatus: 'submit-now', priorityTier: 1.0, lastmod: TODAY },
  { path: '/herramientas', cluster: 'hub', primaryIntent: 'tools-hub', indexStatus: 'index', sitemapGroup: 'core', submissionStatus: 'submit-now', priorityTier: 0.9, lastmod: TODAY },
  { path: '/quitar-fondo', cluster: 'tool', primaryIntent: 'background-removal', indexStatus: 'index', sitemapGroup: 'core', submissionStatus: 'submit-now', priorityTier: 0.8, lastmod: TODAY },
  { path: '/faq', cluster: 'info', primaryIntent: 'faq', indexStatus: 'index', sitemapGroup: 'core', submissionStatus: 'submit-now', priorityTier: 0.7, lastmod: TODAY },
  { path: '/about', cluster: 'info', primaryIntent: 'about', indexStatus: 'index', sitemapGroup: 'core', submissionStatus: 'submit-now', priorityTier: 0.5, lastmod: TODAY },
  { path: '/contact', cluster: 'info', primaryIntent: 'contact', indexStatus: 'index', sitemapGroup: 'core', submissionStatus: 'submit-now', priorityTier: 0.5, lastmod: TODAY },
  { path: '/blog', cluster: 'blog', primaryIntent: 'blog-hub', indexStatus: 'index', sitemapGroup: 'blog', submissionStatus: 'submit-now', priorityTier: 0.8, lastmod: TODAY },
  { path: '/privacidad', cluster: 'legal', primaryIntent: 'privacy', indexStatus: 'index', sitemapGroup: 'legal', submissionStatus: 'hold', priorityTier: 0.3, lastmod: TODAY },
  { path: '/terminos', cluster: 'legal', primaryIntent: 'terms', indexStatus: 'index', sitemapGroup: 'legal', submissionStatus: 'hold', priorityTier: 0.3, lastmod: TODAY },
  { path: '/auth', cluster: 'system', primaryIntent: 'auth', indexStatus: 'noindex', sitemapGroup: 'none', submissionStatus: 'exclude', priorityTier: 0, lastmod: TODAY },
  { path: '/configuration', cluster: 'system', primaryIntent: 'config', indexStatus: 'noindex', sitemapGroup: 'none', submissionStatus: 'exclude', priorityTier: 0, lastmod: TODAY },
  { path: '/analytics', cluster: 'system', primaryIntent: 'analytics', indexStatus: 'noindex', sitemapGroup: 'none', submissionStatus: 'exclude', priorityTier: 0, lastmod: TODAY },
  { path: '/comprimir-imagen', cluster: 'compression', primaryIntent: 'comprimir-imagen', indexStatus: 'index', sitemapGroup: 'landing', submissionStatus: 'submit-now', priorityTier: 0.95, lastmod: TODAY },
];

// ─── Build full registry ────────────────────────────────────────────────────
const toolClassifications = [...new Set(allToolSlugs)].map(classifyTool);
const allClassifications: URLClassification[] = [...staticPages, ...toolClassifications];

// ─── Exports ────────────────────────────────────────────────────────────────
const classificationMap = new Map(allClassifications.map(c => [c.path, c]));

export function getClassification(pathOrSlug: string): URLClassification | undefined {
  const path = pathOrSlug.startsWith('/') ? pathOrSlug : `/herramientas/${pathOrSlug}`;
  return classificationMap.get(path);
}

export function getRedirectTarget(slug: string): string | undefined {
  return redirectMap[slug];
}

export function isNoindex(slug: string): boolean {
  return slug in noindexPages;
}

export function getCanonicalOverride(slug: string): string | undefined {
  return noindexPages[slug];
}

export function getCoreToolSlugs(): string[] {
  return [...coreToolSlugs];
}

export function getToolsByGroup(group: SitemapGroup): URLClassification[] {
  return allClassifications.filter(c => c.sitemapGroup === group);
}

export { coreToolSlugs, noindexPages, allClassifications };
